// API client for RSP backend
const API_BASE = import.meta.env.VITE_API_URL ?? 'https://rsp-workers.w315194731.workers.dev';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PresignResponse {
  job_id: string;
  upload_url: string;
  method: string;
  headers: Record<string, string>;
}

/** Alias for backwards compat */
export type UploadResponse = { job_id: string };

export interface JobStatusResponse {
  job_id: string;
  status: 'queued' | 'pending' | 'processing' | 'done' | 'failed';
  progress?: number;       // 0–100
  total?: number;          // total images
  processed?: number;      // images processed so far
  output_urls?: string[];  // available when done
  error?: string;
  created_at: number;
  updated_at?: number;
}

// ─── Upload ──────────────────────────────────────────────────────────────────

/**
 * Upload images using presigned R2 URL flow:
 * 1. Get presign for each file → PUT to R2 → POST /api/job to start
 * Returns array of job_ids (one per file).
 */
export async function uploadImages(
  files: File[],
  onProgress?: (loaded: number, total: number) => void
): Promise<{ job_id: string }[]> {
  const jobIds: string[] = [];
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  let uploadedBytes = 0;

  for (const file of files) {
    // Step 1: Get presigned R2 upload URL
    const presignRes = await fetch(`${API_BASE}/api/upload/presign`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        operation: 'bg_removal',
      }),
    });

    if (!presignRes.ok) {
      const err = await presignRes.text();
      throw new Error(`Presign failed: ${presignRes.status} ${err}`);
    }

    const presign: PresignResponse = await presignRes.json();

    // Step 2: PUT file directly to R2 via presigned URL
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(presign.method, presign.upload_url);
      Object.entries(presign.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const totalWithOthers = uploadedBytes + e.loaded;
          onProgress(totalWithOthers, totalBytes);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          uploadedBytes += file.size;
          onProgress?.(uploadedBytes, totalBytes);
          resolve();
        } else {
          reject(new Error(`R2 upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error during R2 upload')));
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
      xhr.send(file);
    });

    // Step 3: Tell backend the file is uploaded → start processing
    const jobRes = await fetch(`${API_BASE}/api/job`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ job_id: presign.job_id, operation: 'bg_removal' }),
    });

    if (!jobRes.ok) {
      const err = await jobRes.text();
      throw new Error(`Job start failed: ${jobRes.status} ${err}`);
    }

    jobIds.push(presign.job_id);
  }

  return jobIds.map(job_id => ({ job_id }));
}

// ─── Job Status ──────────────────────────────────────────────────────────────

/**
 * Poll job status until done/failed.
 */
export async function pollJobStatus(
  jobId: string,
  onProgress?: (status: JobStatusResponse) => void,
  intervalMs = 2000
): Promise<JobStatusResponse> {
  return new Promise((resolve, reject) => {
    const tick = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/job/${jobId}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`Status poll failed: ${res.status}`);
        const data = await res.json();

        // Normalize response shape to match frontend expectations
        const normalized: JobStatusResponse = {
          job_id: data.id ?? jobId,
          status: data.status,
          progress: data.progress,
          total: data.total,
          processed: data.processed,
          output_urls: data.output_url ? [data.output_url] : data.output_urls,
          error: data.error_message ?? data.error,
          created_at: data.created_at,
          updated_at: data.updated_at ?? data.completed_at,
        };

        onProgress?.(normalized);

        if (normalized.status === 'done' || normalized.status === 'failed') {
          resolve(normalized);
        } else {
          setTimeout(tick, intervalMs);
        }
      } catch (err) {
        reject(err);
      }
    };
    tick();
  });
}

// ─── Download ────────────────────────────────────────────────────────────────

/**
 * Get download URL for a single output file.
 */
export function getDownloadUrl(jobId: string, filename: string): string {
  return `${API_BASE}/api/job/${jobId}/download?filename=${encodeURIComponent(filename)}`;
}

/**
 * Download a single file via anchor click.
 */
export function downloadFile(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
}

/**
 * Trigger batch zip download.
 */
export function downloadBatchZip(jobId: string): void {
  window.open(`${API_BASE}/api/job/${jobId}/download`, '_blank');
}